import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Axios from 'axios';
import * as Yup from 'yup';

import {
  Box,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Chip,
  Autocomplete,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

import { useAuth } from 'src/hooks/useAuth';
import { useConfig } from 'src/hooks/useConfig';
import { axiosHeaders, getLocalisedErrorString, appendContactSupport } from 'src/utils/helpers';
import authService from 'src/utils/authService';
import SubmitBtn from 'src/shared/buttons/SubmitBtn';
import CancelBtn from 'src/shared/buttons/CancelBtn';
import styles from './style';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`edit-tabpanel-${index}`}
      aria-labelledby={`edit-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface PropTypes {
  open: boolean;
  handleClose: () => void;
  onProjectUpdated?: () => void;
  onWorkflowUpdated?: () => void;
  selectedItem?: {
    type: 'project' | 'workflow';
    projectId: string;
    workflowId?: string;
    projectName: string;
    projectDescription: string;
    workflowName?: string;
    workflowDescription?: string;
    workflowUsers?: string[];
  };
}

const EditProjectWorkflow = (props: PropTypes) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { API, LOGIN_PANEL_URL } = useConfig();

  const { open, handleClose, onProjectUpdated, onWorkflowUpdated, selectedItem } = props;

  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<Record<string, any>[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [projectValues, setProjectValues] = useState({
    name: '',
    description: ''
  });
  const [workflowValues, setWorkflowValues] = useState({
    name: '',
    description: '',
    users: [] as string[]
  });

  const projectFormRef = useRef<any>(null);
  const workflowFormRef = useRef<any>(null);

  useEffect(() => {
    if (selectedItem) {
      setTabValue(0);

      setProjectValues({
        name: selectedItem.projectName || '',
        description: selectedItem.projectDescription || ''
      });
      setWorkflowValues({
        name: selectedItem.workflowName || '',
        description: selectedItem.workflowDescription || '',
        users: selectedItem.workflowUsers || []
      });
    }
  }, [selectedItem]);

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const fetchUsers = async () => {
    if (!authService.validateToken()) {
      return;
    }

    try {
      const response = await Axios.get(
        `${API.getUsersByCompanies}${user?.companyID}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success && response.data.data) {
        const usersData = response.data.data.map((user: any) => ({
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
        }));
        setUsers(usersData);
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
    }
  };

  const updateProject = async (values: { name: string; description: string }) => {
    if (!authService.validateToken()) {
      enqueueSnackbar(t('PROCYS_LOGIN_SESSION_EXPIRED'), {
        variant: 'error',
      });
      setTimeout(() => {
        authService.logout(LOGIN_PANEL_URL);
      }, 2000);
      return false;
    }

    if (!selectedItem?.projectId) {
      enqueueSnackbar(t('PROJECT_UPDATE_ERROR'), {
        variant: 'error',
      });
      return false;
    }

    try {
      const requestBody = {
        companyId: parseInt(user?.companyID || '0'),
        id: selectedItem.projectId,
        name: values.name,
        description: values.description,
      };

      const response = await Axios.put(
        `${API.createProject}/${selectedItem.projectId}`,
        requestBody,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success) {
        enqueueSnackbar(t('PROJECT_UPDATE_SUCCESS'), {
          variant: 'success',
        });
        if (onProjectUpdated) {
          onProjectUpdated();
        }
        return true;
      } else {
        enqueueSnackbar(t('PROJECT_UPDATE_FAILED'), {
          variant: 'error',
        });
        return false;
      }
    } catch (error: any) {
      console.error('Error updating project:', error);
      enqueueSnackbar(
        appendContactSupport(
          window.config.support_email,
          getLocalisedErrorString(error?.response?.data?.i18n || 'PROJECT_UPDATE_FAILED', t),
          t
        ),
        {
          variant: 'error',
        }
      );
      return false;
    }
  };

  const updateWorkflow = async (values: { name: string; description: string; users: string[] }) => {
    if (!authService.validateToken()) {
      enqueueSnackbar(t('PROCYS_LOGIN_SESSION_EXPIRED'), {
        variant: 'error',
      });
      setTimeout(() => {
        authService.logout(LOGIN_PANEL_URL);
      }, 2000);
      return false;
    }

    if (!selectedItem?.projectId || !selectedItem?.workflowId) {
      enqueueSnackbar(t('WORKFLOW_UPDATE_ERROR'), {
        variant: 'error',
      });
      return false;
    }

    try {
      const requestBody = {
        id: selectedItem.workflowId,
        name: values.name,
        description: values.description,
        users: values.users,
      };

      const response = await Axios.put(
        `${API.createProject}/${selectedItem.projectId}/workflows/${selectedItem.workflowId}`,
        requestBody,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success) {
        enqueueSnackbar(t('WORKFLOWS_WORKFLOW_UPDATE_SUCCESS'), {
          variant: 'success',
        });
        if (onWorkflowUpdated) {
          onWorkflowUpdated();
        }
        return true;
      } else {
        enqueueSnackbar(t('WORKFLOW_UPDATE_FAILED'), {
          variant: 'error',
        });
        return false;
      }
    } catch (error: any) {
      console.error('Error updating workflow:', error);
      enqueueSnackbar(
        appendContactSupport(
          window.config.support_email,
          getLocalisedErrorString(error?.response?.data?.i18n || 'WORKFLOW_UPDATE_FAILED', t),
          t
        ),
        {
          variant: 'error',
        }
      );
      return false;
    }
  };

  const validateProjectName = (name: string) => {
    if (!name || name.trim() === '') {
      return t('DOC_TYPE_NAME_REQUIRED_HELPER_TEXT');
    }
    if (name.length < 2) {
      return t('DOC_TYPE_NAME_MIN_HELPER_TEXT');
    }
    if (name.length > 64) {
      return t('DOC_TYPE_NAME_MAX_HELPER_TEXT');
    }
    if (!/^[a-zA-Z0-9][a-zA-Z0-9 _-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/.test(name)) {
      return t('PROJECT_NAME_INVALID_FORMAT');
    }
    return '';
  };

  const validateWorkflowName = (name: string) => {
    if (!name || name.trim() === '') {
      return t('DOC_TYPE_NAME_REQUIRED_HELPER_TEXT');
    }
    if (name.length < 2) {
      return t('DOC_TYPE_NAME_MIN_HELPER_TEXT');
    }
    if (name.length > 64) {
      return t('DOC_TYPE_NAME_MAX_HELPER_TEXT');
    }
    if (!/^[a-zA-Z0-9][a-zA-Z0-9 _-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/.test(name)) {
      return t('WORKFLOW_NAME_INVALID_FORMAT');
    }
    return '';
  };

  const validateDescription = (description: string) => {
    if (!description || description.trim() === '') {
      return t('DOC_TYPE_DESCRIPTION_REQUIRED_HELPER_TEXT');
    }
    if (description.length < 2) {
      return t('WORKFLOWS_DESCRIPTION_MIN_HELPER_TEXT');
    }
    if (description.length > 500) {
      return t('DOC_TYPE_DESCRIPTION_MAX_HELPER_TEXT');
    }
    return '';
  };

  const isProjectFormValid = () => {
    const nameError = validateProjectName(projectValues.name);
    const descriptionError = validateDescription(projectValues.description);
    return !nameError && !descriptionError;
  };

  const isWorkflowFormValid = () => {
    const nameError = validateWorkflowName(workflowValues.name);
    const descriptionError = validateDescription(workflowValues.description);
    return !nameError && !descriptionError;
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      if (tabValue === 0) {
        // Project tab
        if (isProjectFormValid()) {
          const success = await updateProject(projectValues);
          if (success) {
            handleClose();
          }
        } else {
          enqueueSnackbar(t('WORKFLOW_PROJECT_FORM_VALIDATION_ERROR'), {
            variant: 'error',
          });
        }
      } else {
        // Workflow tab
        if (selectedItem?.workflowId && isWorkflowFormValid()) {
          const success = await updateWorkflow(workflowValues);
          if (success) {
            handleClose();
          }
        } else {
          enqueueSnackbar(t('WORKFLOW_PROJECT_FORM_VALIDATION_ERROR'), {
            variant: 'error',
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    if (tabValue === 0) {
      return isProjectFormValid();
    } else {
      return isWorkflowFormValid();
    }
  };

  if (!selectedItem) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={styles.modal}
      onClose={handleClose}
    >
      <Box sx={styles.container}>
        <Box sx={styles.modalHeader}>
          <Box sx={styles.titleContainer}>
            <Typography sx={styles.headerTitle}>
              {t('EDIT_PROJECT_OR_WORKFLOW_TITLE')}
            </Typography>
            <IconButton sx={styles.closeBtn} onClick={handleClose}>
              <CloseIcon sx={styles.closeIcon} />
            </IconButton>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={styles.tabs}
        >
          <Tab
            wrapped
            label={t('EDIT_PROJECT_TAB')}
            id="edit-tab-0"
            aria-controls="edit-tabpanel-0"
          />
          {!selectedItem.workflowId ? (
            <Tooltip
              title={t('WORKFLOW_NOT_AVAILABLE_TOOLTIP')}
              arrow
              placement="top"
            >
              <span>
                <Tab
                  wrapped
                  label={t('EDIT_WORKFLOW_TAB')}
                  id="edit-tab-1"
                  aria-controls="edit-tabpanel-1"
                  disabled={true}
                />
              </span>
            </Tooltip>
          ) : (
            <Tab
              wrapped
              label={t('EDIT_WORKFLOW_TAB')}
              id="edit-tab-1"
              aria-controls="edit-tabpanel-1"
              disabled={false}
            />
          )}
        </Tabs>

        <Box sx={styles.modalContent}>
          {/* Project Edit Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={styles.addNewForm}>
              <Box sx={styles.fieldContainer}>
                <TextField
                  fullWidth
                  label={t('PROJECT_NAME')}
                  name="name"
                  required
                  value={projectValues.name}
                  variant="outlined"
                  error={Boolean(validateProjectName(projectValues.name))}
                  helperText={validateProjectName(projectValues.name)}
                  sx={{
                    ...styles.textField,
                    '& .MuiInputBase-input': {
                      fontSize: '13px',
                    }
                  }}
                  onChange={(e) => {
                    setProjectValues(prev => ({ ...prev, name: e.target.value }));
                    if (projectFormRef.current) {
                      projectFormRef.current.values.name = e.target.value;
                    }
                  }}
                />
              </Box>

              <Box sx={styles.fieldContainer}>
                <TextField
                  fullWidth
                  name="description"
                  label={t('PROJECT_DESCRIPTION')}
                  required
                  value={projectValues.description}
                  variant="outlined"
                  multiline
                  rows={5}
                  sx={styles.textFieldMulti}
                  error={Boolean(validateDescription(projectValues.description))}
                  helperText={validateDescription(projectValues.description)}
                  onChange={(e) => {
                    setProjectValues(prev => ({ ...prev, description: e.target.value }));
                    if (projectFormRef.current) {
                      projectFormRef.current.values.description = e.target.value;
                    }
                  }}
                />
              </Box>
            </Box>
          </TabPanel>

          {/* Workflow Edit Tab */}
          <TabPanel value={tabValue} index={1}>
            {selectedItem.workflowId ? (
              <Box sx={styles.addNewForm}>
                <Box sx={styles.fieldContainer}>
                  <TextField
                    fullWidth
                    label={t('WORKFLOW_NAME')}
                    name="name"
                    required
                    value={workflowValues.name}
                    variant="outlined"
                    error={Boolean(validateWorkflowName(workflowValues.name))}
                    helperText={validateWorkflowName(workflowValues.name)}
                    sx={{
                      ...styles.textField,
                      '& .MuiInputBase-input': {
                        fontSize: '13px',
                      }
                    }}
                    onChange={(e) => {
                      setWorkflowValues(prev => ({ ...prev, name: e.target.value }));
                      if (workflowFormRef.current) {
                        workflowFormRef.current.values.name = e.target.value;
                      }
                    }}
                  />
                </Box>

                <Box sx={styles.fieldContainer}>
                  <TextField
                    fullWidth
                    name="description"
                    label={t('WORKFLOW_DESCRIPTION')}
                    required
                    value={workflowValues.description}
                    variant="outlined"
                    multiline
                    rows={5}
                    sx={styles.textFieldMulti}
                    error={Boolean(validateDescription(workflowValues.description))}
                    helperText={validateDescription(workflowValues.description)}
                    onChange={(e) => {
                      setWorkflowValues(prev => ({ ...prev, description: e.target.value }));
                      if (workflowFormRef.current) {
                        workflowFormRef.current.values.description = e.target.value;
                      }
                    }}
                  />
                </Box>

                <Box sx={styles.fieldContainer}>
                  <Autocomplete
                    multiple
                    id="users-autocomplete"
                    options={users.filter(user => !workflowValues.users.includes(user.email))}
                    value={users.filter(user => workflowValues.users.includes(user.email))}
                    onChange={(event, newValue) => {
                      const userEmails = newValue.map(user => user.email);
                      setWorkflowValues(prev => ({ ...prev, users: userEmails }));
                      if (workflowFormRef.current) {
                        workflowFormRef.current.values.users = userEmails;
                      }
                    }}
                    getOptionLabel={(option) => option.email}
                    isOptionEqualToValue={(option, value) => option.email === value.email}
                    renderInput={(params) => {
                      const chipsCount = workflowValues.users.length;
                      const chipsPerRow = Math.floor(400 / 200);
                      const rows = Math.ceil(chipsCount / chipsPerRow) || 1;
                      const baseHeight = 40;
                      const chipRowHeight = 40;
                      const dynamicHeight = baseHeight + (rows > 1 ? (rows - 1) * chipRowHeight : 0);

                      return (
                        <TextField
                          {...params}
                          label={t('WORKFLOW_ASSIGN_USERS')}
                          sx={{
                            ...styles.autocompleteField,
                            '& .MuiInputBase-root': {
                              minHeight: `${dynamicHeight}px`,
                              height: 'auto',
                              alignItems: 'flex-start',
                              paddingTop: chipsCount > 0 ? '8px' : '16.5px',
                              paddingBottom: '8px',
                              transition: 'min-height 0.2s ease-in-out',
                            },
                            '& .MuiInputLabel-root': {
                              transform: chipsCount > 0
                                ? 'translate(14px, -9px) scale(0.75)'
                                : 'translate(14px, 16px) scale(1)',
                              '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -9px) scale(0.75)',
                              }
                            }
                          }}
                          FormHelperTextProps={{
                            sx: { fontSize: '11px' }
                          }}
                        />
                      );
                    }}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.email}
                          label={option.email}
                          sx={styles.userChip}
                          deleteIcon={<CloseIcon />}
                        />
                      ))
                    }
                    renderOption={(props, option) => (
                      <li {...props} style={{ fontSize: '13px', padding: '8px 16px' }}>
                        <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: '400' }}>
                          {option.email}
                        </Typography>
                      </li>
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: '200px',
                        fontSize: '13px'
                      }
                    }}
                    clearIcon={null}
                    disableCloseOnSelect
                    sx={{
                      ...styles.autocompleteField,
                      '& .MuiAutocomplete-inputRoot': {
                        flexWrap: 'wrap',
                        paddingRight: '39px !important',
                      },
                      '& .MuiAutocomplete-input': {
                        minWidth: '120px',
                        fontSize: '13px',
                      },
                      '& .MuiAutocomplete-tag': {
                        margin: '2px',
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {t('NO_WORKFLOW_SELECTED')}
                </Typography>
              </Box>
            )}
          </TabPanel>
        </Box>

        <Box sx={styles.modalFooter}>
          <Box display="flex" justifyContent="flex-end" gap="16px">
            <CancelBtn
              title={t('DOC_TYPES_TABLE_ADD_NEW_CANCEL')}
              onClick={handleClose}
            />
            <SubmitBtn
              title={t('EDIT_PROJECT_OR_WORKFLOW_SAVE')}
              disabled={isSubmitting || !isFormValid()}
              onClick={handleSave}
            />
          </Box>
        </Box>
      </Box>

      <div style={{ display: 'none' }}>
        <div ref={(el) => {
          if (el && !projectFormRef.current) {
            projectFormRef.current = {
              values: projectValues,
              errors: {},
              validateForm: async () => {
                const schema = Yup.object().shape({
                  name: Yup.string()
                    .min(2, t('DOC_TYPE_NAME_MIN_HELPER_TEXT'))
                    .max(64, t('DOC_TYPE_NAME_MAX_HELPER_TEXT'))
                    .matches(/^[a-zA-Z0-9][a-zA-Z0-9 _-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/, t('PROJECT_NAME_INVALID_FORMAT'))
                    .required(t('DOC_TYPE_NAME_REQUIRED_HELPER_TEXT')),
                  description: Yup.string()
                    .min(2, t('WORKFLOWS_DESCRIPTION_MIN_HELPER_TEXT'))
                    .max(500, t('DOC_TYPE_DESCRIPTION_MAX_HELPER_TEXT'))
                    .required(t('DOC_TYPE_DESCRIPTION_REQUIRED_HELPER_TEXT'))
                });

                try {
                  await schema.validate(projectFormRef.current.values, { abortEarly: false });
                  projectFormRef.current.errors = {};
                  return {};
                } catch (err: any) {
                  const errors: any = {};
                  err.inner.forEach((error: any) => {
                    errors[error.path] = error.message;
                  });
                  projectFormRef.current.errors = errors;
                  return errors;
                }
              }
            };
          }
        }} />

        <div ref={(el) => {
          if (el && !workflowFormRef.current) {
            workflowFormRef.current = {
              values: workflowValues,
              errors: {},
              validateForm: async () => {
                const schema = Yup.object().shape({
                  name: Yup.string()
                    .min(2, t('DOC_TYPE_NAME_MIN_HELPER_TEXT'))
                    .max(64, t('DOC_TYPE_NAME_MAX_HELPER_TEXT'))
                    .matches(/^[a-zA-Z0-9][a-zA-Z0-9 _-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/, t('WORKFLOW_NAME_INVALID_FORMAT'))
                    .required(t('DOC_TYPE_NAME_REQUIRED_HELPER_TEXT')),
                  description: Yup.string()
                    .min(2, t('WORKFLOWS_DESCRIPTION_MIN_HELPER_TEXT'))
                    .max(500, t('DOC_TYPE_DESCRIPTION_MAX_HELPER_TEXT'))
                    .required(t('DOC_TYPE_DESCRIPTION_REQUIRED_HELPER_TEXT')),
                  users: Yup.array()
                    .of(Yup.string())
                    .min(0, t('WORKFLOW_USERS_REQUIRED'))
                });

                try {
                  await schema.validate(workflowFormRef.current.values, { abortEarly: false });
                  workflowFormRef.current.errors = {};
                  return {};
                } catch (err: any) {
                  const errors: any = {};
                  err.inner.forEach((error: any) => {
                    errors[error.path] = error.message;
                  });
                  workflowFormRef.current.errors = errors;
                  return errors;
                }
              }
            };
          }
        }} />
      </div>
    </Drawer>
  );
};

export default EditProjectWorkflow;
